import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../../components/ui';
import { SplitViewTabBar } from '../../components/generic/SplitViewWithTabs';
import ReturnedProductInspectionDetailsTab from './components/tabs/ReturnedProductInspectionDetailsTab';
import ReturnedProductInspectionAuditLogTab from './components/tabs/ReturnedProductInspectionAuditLogTab';
import NotifyForSignatureButton from './components/NotifyForSignatureButton';
import { useFlexibleList } from '../../hooks/useSWR';
import { DocumentTextIcon, ClipboardCheckIcon, XIcon } from '../../components/ui/Icons';

/**
 * Custom Split View: Details + Audit Log.
 */
const CustomReturnedProductInspectionSplitView = ({
  isOpen,
  selectedRecord,
  selectedRecordFiles,
  config,
  fields,
  labels,
  onClose,
  onRecordUpdate,
}) => {
  const { theme } = useTheme();

  const { data: auditLogs = [] } = useFlexibleList(
    selectedRecord?.id
      ? `/audit-log/master/${config?.features?.masterEntityTypeId || 21}/${selectedRecord.id}`
      : null,
  );

  const { data: fetchedFileAttachments = [] } = useFlexibleList(
    config?.features?.fileAttachments && selectedRecord?.id
      ? `/file-attachment/entity/${config?.entityTypeId || 21}/${selectedRecord.id}`
      : null,
  );

  const fileAttachments = (
    fetchedFileAttachments.length > 0
      ? fetchedFileAttachments
      : selectedRecordFiles || []
  ).filter((file) => !file.isDeleted);

  const allTabs = [
    {
      value: 'details',
      label: 'Details',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge:
        config?.features?.fileAttachments && fileAttachments.length > 0
          ? fileAttachments.length
          : null,
      badgeColor:
        config?.features?.fileAttachments && fileAttachments.length > 0
          ? 'bg-blue-600'
          : null,
    },
    {
      value: 'audit-log',
      label: 'Audit Log',
      icon: <ClipboardCheckIcon className="w-4 h-4" />,
      badge: auditLogs.length,
      badgeColor: 'bg-red-600',
    },
  ];

  if (!isOpen) return null;

  const titleLabel = selectedRecord
    ? [
        selectedRecord.productName,
        selectedRecord.batchNumber ? `Lot ${selectedRecord.batchNumber}` : null,
      ]
        .filter(Boolean)
        .join(' · ') || `Inspection #${selectedRecord.id}`
    : null;

  return (
    <div
      className={cn('hidden lg:block min-h-0 h-full pb-2')}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <div
        className={cn(
          'min-h-0 h-full rounded-xl border flex flex-col',
          theme.border.light,
          theme.colors.surface,
          theme.shadow.md,
        )}
      >
        <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
          <div className={cn('text-sm font-semibold min-w-0 truncate', theme.text.primary)}>
            {selectedRecord
              ? `Returned Product — ${titleLabel}`
              : 'Select a returned product inspection to view details'}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {selectedRecord ? (
              <NotifyForSignatureButton record={selectedRecord} />
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className={cn('p-1 rounded hover:opacity-80', theme.text.secondary)}
              title={labels.closeText || 'Close'}
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {selectedRecord ? (
          <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0 px-3 py-2">
            <SplitViewTabBar tabs={allTabs} />

            <div className="flex-1 flex flex-col min-h-0">
              <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
                <ReturnedProductInspectionDetailsTab
                  selectedRecord={selectedRecord}
                  selectedRecordFiles={fileAttachments}
                  config={config}
                  fields={fields}
                  onRecordUpdate={onRecordUpdate}
                />
              </TabsContent>

              <TabsContent value="audit-log" className="mt-0 flex-1 flex flex-col min-h-0">
                <ReturnedProductInspectionAuditLogTab
                  selectedRecord={selectedRecord}
                  config={config}
                />
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className={cn('p-4 text-sm', theme.text.secondary)}>
            {labels.noSelectionText || 'No returned product inspection selected'}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomReturnedProductInspectionSplitView;
